import React from 'react';
import styled from 'styled-components';

import {getRentalClient} from '../../api/rental';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  DefinitionList,
  ErrorText,
  Field,
  Input,
  Scroll,
  State,
  Table,
} from './styles';
import {checkTitle, outcomeMeta, riskLabel, riskLevelMeta, statusMeta} from './verification-view';

const KYC_STATUSES = {
  none: 'документы не подавались',
  draft: 'черновик',
  pending: 'на проверке',
  approved: 'одобрены',
  rejected: 'отклонены',
};

const RENTAL_STATUSES = {
  payment_pending: 'Оплата',
  reserved: 'Забронирована',
  active: 'Активна',
  inspection: 'Осмотр',
  completed: 'Завершена',
  canceled: 'Отменена',
  expired: 'Истекла',
  payment_failed: 'Ошибка оплаты',
};

const CHARGE_CATEGORIES = {
  cleaning: 'Мойка/химчистка',
  refueling: 'Дозаправка/зарядка',
  traffic_fine: 'Штраф ГИБДД',
  damage: 'Повреждение',
  other: 'Другое',
};

const money = value => `${(Number(value || 0) / 100).toLocaleString('ru-RU')} ₽`;
const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—');
const formatDay = value => (value
  ? new Date(value).toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: '2-digit'})
  : '—');

/** Идентификатор клиента приходит из адреса: карточка открывается из аренды. */
const userIdFromPath = () => {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[2] || '';
};

function ClientsContent({authorization}) {
  const [userId, setUserId] = React.useState(userIdFromPath);
  const [query, setQuery] = React.useState(userIdFromPath);
  const [data, setData] = React.useState(null);
  const [state, setState] = React.useState(userIdFromPath() ? 'loading' : 'idle');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!authorization || !userId) { return; }
    setState('loading');
    setError('');
    try {
      setData(await getRentalClient(authorization, userId));
      setState('ready');
    } catch (requestError) {
      setError(requestError.message);
      setData(null);
      setState('ready');
    }
  }, [authorization, userId]);

  React.useEffect(() => { load(); }, [load]);

  const search = event => {
    event.preventDefault();
    setUserId(query.trim());
    window.history.replaceState(null, '', `/rental/clients/${query.trim()}`);
  };

  const client = data && data.client;

  return (
    <div>
      <Card>
        <CardTitle>Клиент</CardTitle>
        <form onSubmit={search}>
          <Field>
            Идентификатор пользователя
            <Input
              value={query}
              placeholder="UUID из карточки аренды"
              onChange={event => setQuery(event.target.value)}
            />
          </Field>
          <Actions>
            <ActionButton type="submit" primary disabled={!query.trim()}>Открыть</ActionButton>
          </Actions>
        </form>

        {error && <ErrorText>{error}</ErrorText>}
        {state === 'idle' && (
          <State>Откройте карточку из аренды или введите идентификатор клиента.</State>
        )}
        {state === 'loading' && <State>Загрузка…</State>}

        {client && (
          <div style={{marginTop: 24}}>
            <Badges>
              <Badge success={client.kyc_status === 'approved'} danger={client.kyc_status === 'rejected'}>
                Документы: {KYC_STATUSES[client.kyc_status] || client.kyc_status}
              </Badge>
              <Badge danger={client.debt_amount > 0} success={client.debt_amount === 0}>
                Задолженность: {money(client.debt_amount)}
              </Badge>
              {client.risk && (
                <Badge
                  danger={['critical', 'high'].includes(client.risk.level)}
                  success={client.risk.level === 'low'}
                  title={riskLevelMeta(client.risk.level).advice}
                >
                  Риск: {riskLabel(client.risk)}
                </Badge>
              )}
              {client.deleted && <Badge danger>Аккаунт удалён</Badge>}
            </Badges>
            {client.risk && client.risk.summary && (
              <RiskSummaryBlock>
                <strong>Почему такой уровень риска</strong>
                <pre>{client.risk.summary}</pre>
              </RiskSummaryBlock>
            )}

            <DefinitionList>
              <dt>Телефон</dt><dd>{client.phone || '—'}</dd>
              <dt>Имя</dt>
              <dd>{[client.last_name, client.first_name].filter(Boolean).join(' ') || '—'}</dd>
              <dt>Почта</dt><dd>{client.email || '—'}</dd>
              <dt>Регистрация</dt><dd>{formatDate(client.registered_at)}</dd>
              <dt>Карта проката</dt>
              <dd>
                {client.payment_method_title
                  ? `${client.payment_method_title} · ${client.payment_provider}`
                  : 'не привязана'}
              </dd>
              <dt>Версия документов</dt><dd>{client.kyc_version || '—'}</dd>
              <dt>Проверены</dt><dd>{formatDate(client.kyc_reviewed_at)}</dd>
              <dt>Причина отказа</dt><dd>{client.kyc_rejection_reason || '—'}</dd>
            </DefinitionList>
          </div>
        )}
      </Card>

      {data && (
        <Card style={{marginTop: 24}}>
          <CardTitle>История аренд</CardTitle>
          {data.rentals.length === 0 && <State>Аренд пока не было.</State>}
          {data.rentals.length > 0 && (
            <Scroll>
              <Table minWidth="900px">
                <thead>
                  <tr>
                    <th>Период</th><th>Автомобиль</th><th>Пункт</th><th>Статус</th>
                    <th>Оплачено</th><th>Депозит</th><th>Долг</th><th />
                  </tr>
                </thead>
                <tbody>
                  {data.rentals.map(rental => (
                    <tr key={rental.id}>
                      <td>{formatDay(rental.starts_at)} — {formatDay(rental.ends_at)}</td>
                      <td>
                        {rental.vehicle.brand} {rental.vehicle.model}
                        <div>{rental.vehicle.license_plate}</div>
                      </td>
                      <td>{rental.location ? `${rental.location.city} · ${rental.location.title}` : '—'}</td>
                      <td>
                        <Badge
                          success={rental.status === 'completed'}
                          danger={rental.payment_status === 'debt'}
                        >
                          {RENTAL_STATUSES[rental.status] || rental.status}
                        </Badge>
                      </td>
                      <td>{money(rental.prepaid_amount)}</td>
                      <td>
                        {money(rental.deposit_amount)}
                        <div>вернули {money(rental.deposit_refunded_amount)}</div>
                      </td>
                      <td>{money(rental.debt_amount)}</td>
                      <td>
                        <a href={`/rental/rentals?rental=${rental.id}`}>Открыть</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Scroll>
          )}
        </Card>
      )}

      {data && data.charges.length > 0 && (
        <Card style={{marginTop: 24}}>
          <CardTitle>Начисления</CardTitle>
          <Scroll>
            <Table minWidth="720px">
              <thead>
                <tr><th>Дата</th><th>Категория</th><th>Комментарий</th><th>Сумма</th><th>Статус</th></tr>
              </thead>
              <tbody>
                {data.charges.map(charge => (
                  <tr key={charge.id}>
                    <td>{formatDate(charge.created_at)}</td>
                    <td>{CHARGE_CATEGORIES[charge.category] || charge.category}</td>
                    <td>{charge.comment}</td>
                    <td>{money(charge.amount)}</td>
                    <td>
                      <Badge danger={charge.status === 'debt'} success={charge.status === 'paid'}>
                        {charge.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        </Card>
      )}

      {data && data.verification_checks.length > 0 && (
        <Card style={{marginTop: 24}}>
          <CardTitle>Внешние проверки</CardTitle>
          <Scroll>
            <Table minWidth="640px">
              <thead>
                <tr><th>Дата</th><th>Проверка</th><th>Статус</th><th>Итог</th></tr>
              </thead>
              <tbody>
                {data.verification_checks.map(check => (
                  <tr key={check.id}>
                    <td>{formatDate(check.created_at)}</td>
                    <td>{checkTitle(check.check_type)}</td>
                    <td>{statusMeta(check.status).label}</td>
                    <td>
                      <Badge
                        danger={outcomeMeta(check).tone === 'attention'}
                        success={outcomeMeta(check).tone === 'clear'}
                      >
                        {outcomeMeta(check).label}
                      </Badge>
                      <div style={{marginTop: 6}}>{check.summary || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        </Card>
      )}
    </div>
  );
}

function Clients() {
  return (
    <Layout
      title="Карточка клиента"
      description="Профиль, история аренд, начисления и задолженность в одном месте — чтобы не собирать картину по вкладкам."
    >
      <ClientsContent />
    </Layout>
  );
}

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const RiskSummaryBlock = styled.div`
  margin: -8px 0 20px;
  padding: 12px 14px;
  border-left: 4px solid #e6eaed;
  border-radius: 4px;
  background: #f5f7f9;
  font-size: 13px;
  line-height: 1.5;
  pre {
    margin: 6px 0 0;
    font: inherit;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

export default Clients;
